import { useState, useEffect } from 'react';
import { Player } from '@/types';

interface BestCatcherSelectionModalProps {
  teamAName: string;
  teamBName: string;
  teamAColor: string;
  teamBColor: string;
  playersTeamA: Player[];
  playersTeamB: Player[];
  initiallySelectedIds: string[];
  onSave: (selectedPlayerIds: string[]) => Promise<void>;
  onCancel: () => void;
  setNumber?: number;
  isFinal?: boolean;
}

export default function BestCatcherSelectionModal({
  teamAName,
  teamBName,
  teamAColor,
  teamBColor,
  playersTeamA,
  playersTeamB,
  initiallySelectedIds,
  onSave,
  onCancel,
  setNumber,
  isFinal = false,
}: BestCatcherSelectionModalProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(initiallySelectedIds));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setSelectedIds(new Set(initiallySelectedIds));
  }, [initiallySelectedIds]);

  const togglePlayer = (playerId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(playerId)) {
        next.delete(playerId);
      } else {
        next.add(playerId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(Array.from(selectedIds));
    } catch (err) {
      console.error('Failed to save Best Catcher:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-border rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto animate-fade-in">
        <h2 className="text-xl font-bold text-text-primary text-center mb-2">
          {isFinal ? 'FINAL BEST CATCHER' : 'BEST CATCHER'}
        </h2>
        {setNumber !== undefined && !isFinal && (
          <p className="text-text-secondary text-sm text-center mb-4">
            After Set {setNumber}
          </p>
        )}
        {isFinal && (
          <p className="text-text-secondary text-sm text-center mb-4">
            Confirm final Best Catcher selections
          </p>
        )}

        <p className="text-text-muted text-xs text-center mb-6">
          Select players from both teams
        </p>

        {/* Team A Players */}
        <div className="mb-4">
          <h3 className="text-xs font-semibold uppercase mb-2" style={{ color: teamAColor }}>
            {teamAName}
          </h3>
          <div className="space-y-2">
            {playersTeamA.map(player => (
              <button
                key={player.id}
                onClick={() => togglePlayer(player.id)}
                className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                  selectedIds.has(player.id)
                    ? 'bg-team-a/20 border-team-a'
                    : 'bg-surface-raised border-border hover:border-text-muted'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-semibold" style={{ color: teamAColor }}>{player.name}</span>
                    <span className="text-text-muted ml-2">#{player.number}</span>
                  </div>
                  {selectedIds.has(player.id) && (
                    <span className="w-5 h-5 rounded-full bg-team-a flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Team B Players */}
        <div className="mb-6">
          <h3 className="text-xs font-semibold uppercase mb-2" style={{ color: teamBColor }}>
            {teamBName}
          </h3>
          <div className="space-y-2">
            {playersTeamB.map(player => (
              <button
                key={player.id}
                onClick={() => togglePlayer(player.id)}
                className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                  selectedIds.has(player.id)
                    ? 'bg-team-b/20 border-team-b'
                    : 'bg-surface-raised border-border hover:border-text-muted'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-semibold" style={{ color: teamBColor }}>{player.name}</span>
                    <span className="text-text-muted ml-2">#{player.number}</span>
                  </div>
                  {selectedIds.has(player.id) && (
                    <span className="w-5 h-5 rounded-full bg-team-b flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Selected Count */}
        <p className="text-text-muted text-xs text-center mb-4">
          {selectedIds.size} player{selectedIds.size !== 1 ? 's' : ''} selected
        </p>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="btn-primary bg-team-a text-white font-bold py-4"
          >
            {isSaving ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                SAVING...
              </span>
            ) : (
              'SAVE'
            )}
          </button>
          <button
            onClick={onCancel}
            disabled={isSaving}
            className="btn-secondary text-text-secondary"
          >
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
}
